import moment from 'moment';
import * as React from 'react';
import useHttp from '@/hooks/useHttp';
import { formatToHumanNumber } from '@/helpers/formatter';

import Slide from '@mui/material/Slide';
import Dialog from '@mui/material/Dialog';
import { Close } from '@mui/icons-material';
import { green } from '@mui/material/colors';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Box, Chip, Divider, IconButton, Typography } from '@mui/material';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function ModalTopBuyers({
    open,
    onClose,
    raffle,
    showDaily = false
}) {
    const { get } = useHttp();
    const [payments, setPayments] = React.useState(null);

    const handleClose = () => {
        onClose();
    };

    const getTopTen = async () => {
        const response = await get(`/api/payment/${raffle.slug}/top-10-buyers${showDaily == true ? "-daily" : ""}`);
        if (response) {
            setPayments(response);
        }
    }

    React.useEffect(() => {
        if (!raffle?.slug) {
            return;
        }
        getTopTen();
    }, [raffle, open]);

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            aria-describedby="alert-dialog-slide-description"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: { xs: 1, sm: 2 },
                }}>
                    <Typography sx={{ fontSize: { xs: "2rem", sm: "2.5rem" } }}>🏆</Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant='h5' sx={{
                            fontSize: { xs: "1.25rem", sm: "1.5rem" },
                            mb: 0.5
                        }}>
                            Top Compradores {showDaily && "Diário"}
                        </Typography>
                        <Typography variant='body2' sx={{
                            color: "text.secondary",
                            fontSize: { xs: "0.75rem", sm: "0.875rem" }
                        }}>
                            Maiores compradores no sorteio <b>{raffle?.title}</b>
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8
                        }}
                    >
                        <Close />
                    </IconButton>
                </Box>
                {showDaily && raffle.top_daily_buyers_start_datetime && (
                    <Box sx={{ mt: 2 }}>
                        <RenderDailyDate
                            topDailyBuyersStartDatetime={raffle.top_daily_buyers_start_datetime}
                            topDailyBuyersEndDatetime={raffle.top_daily_buyers_end_datetime}
                        />
                    </Box>
                )}
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
                {
                    !!payments?.length && payments.map((item, index) => {
                        if (index == 0) {
                            return (
                                <React.Fragment key={index}>
                                    <FirstPlaceClient name={item.name} phone={item.phone} quantity={item.quantity} />
                                    <Divider />
                                </React.Fragment>
                            );
                        }
                        return (
                            <React.Fragment key={index}>
                                <OthersPlaceClient place={index + 1} name={item.name} phone={item.phone} quantity={item.quantity} />
                                <Divider />
                            </React.Fragment>
                        );
                    })
                }
                {
                    payments?.length == 0 && <div style={{ display: "flex", justifyContent: "center" }}>
                        <Typography sx={{ textAlign: "center", py: 2, color: "rgba(0,0,0, .7)", alignSelf: "center" }} variant='caption'>Nenhum ganhador 😔</Typography>
                    </div>
                }
            </DialogContent>

        </Dialog>
    );
}


const FirstPlaceClient = ({ name, phone, quantity }) => {
    return (
        <Box
            display={"flex"}
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent={"space-between"}
            gap={{ xs: 1, sm: 0 }}
            my={1}
            sx={{
                bgcolor: green[50],
                p: 2,
                borderRadius: 2,
                border: `2px solid ${green[200]}`
            }}
        >
            <Box display={"flex"} gap={1}>
                <Typography
                    variant='h2'
                    color={green[800]}
                    sx={{
                        minWidth: { xs: "2.5rem", sm: "3.75rem" },
                        fontSize: { xs: "2rem", sm: "3rem" }
                    }}
                >
                    1º
                </Typography>
                <Box display={"flex"} flexDirection={"column"} justifyContent={"center"}>
                    <Typography
                        variant='subtitle1'
                        color={green[800]}
                        sx={{
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                            fontWeight: 600
                        }}
                    >
                        {name}
                    </Typography>
                    <Typography
                        color={green[800]}
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                        {phone}
                    </Typography>
                </Box>
            </Box>
            <Box
                display={"flex"}
                justifyContent={{ xs: "flex-start", sm: "center" }}
                alignItems={"center"}
                sx={{ ml: { xs: 5, sm: 0 } }}
            >
                <Chip
                    label={`${formatToHumanNumber(quantity)} títulos`}
                    color="success"
                    sx={{ fontWeight: 600 }}
                />
            </Box>
        </Box>
    );
}

const OthersPlaceClient = ({ place, name, phone, quantity }) => {
    return (
        <Box
            display={"flex"}
            flexDirection={{ xs: "column", sm: "row" }}
            justifyContent={"space-between"}
            gap={{ xs: 1, sm: 0 }}
            my={1.5}
            py={1}
        >
            <Box display={"flex"} gap={1}>
                <Box sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    minWidth: { xs: "2.5rem", sm: "3.75rem" }
                }}>
                    <Typography
                        variant='h4'
                        sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
                    >
                        {place}º
                    </Typography>
                </Box>
                <Box display={"flex"} flexDirection={"column"} justifyContent={"center"}>
                    <Typography
                        variant='subtitle1'
                        sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                    >
                        {name}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        {phone}
                    </Typography>
                </Box>
            </Box>
            <Box
                display={"flex"}
                justifyContent={{ xs: "flex-start", sm: "center" }}
                alignItems={"center"}
                sx={{ ml: { xs: 5, sm: 0 } }}
            >
                <Typography
                    variant='body2'
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                    {formatToHumanNumber(quantity)} títulos
                </Typography>
            </Box>
        </Box>
    );
}

const RenderDailyDate = ({
    topDailyBuyersStartDatetime,
    topDailyBuyersEndDatetime
}) => {
    const startDate = moment(topDailyBuyersStartDatetime);
    const endDate = topDailyBuyersEndDatetime ? moment(topDailyBuyersEndDatetime) : null;
    const isSameDay = endDate && startDate.isSame(endDate, 'day');

    return (
        <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            justifyContent: { xs: "center", sm: "center" },
            textAlign: "center"
        }}>
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                alignSelf: "center",
                flexDirection: "row",
                lineHeight: 1
            }}>
                <Chip
                    label={`${startDate.format("DD/MM/YYYY")}${endDate ? ` às ${startDate.format("HH:mm")}` : ''}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: "0.6rem", alignSelf: "center" }}
                />
                {
                    endDate &&
                    <div style={{ textAlign: "center", width: "100%", marginLeft: 5, marginRight: 5 }}>
                        <Typography variant="caption" sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" }, mx: { xs: 0, sm: 0.5 } }}>
                            até
                        </Typography>
                    </div>
                }
                {endDate && (
                    <div>
                        <Chip
                            label={
                                isSameDay
                                    ? `${endDate.format("HH:mm")}`
                                    : `${endDate.format("DD/MM/YYYY")} às ${endDate.format("HH:mm")}`
                            }
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: "0.6rem", alignSelf: "center" }}
                        />
                    </div>
                )}
            </Box>
        </Box>
    );
}
